<script setup lang="ts">
import { ref, computed, watch } from "vue";
import type { PhotoDocument } from "@kekkle/shared/types/documents/photo-document/photo-document";
import { ChatAlt2Icon, ExclamationIcon } from "@heroicons/vue/outline";
import { ImageSizes } from "@/enums/images-sizes";
import {
  getBlurhashUrlSync,
  requestBlurhashUrl,
} from "@/helpers/blurhash/blurhash-url-queue";
import { usePhotosStore } from "@/stores/modules/photos/photos";
import { useElementVisibility } from "@/composables/useElementVisibility";
import TinyUserThumb from "@/components/TinyUserThumb.vue";

type Props = {
  photoDoc: PhotoDocument;
  groupId: string;
  /** Activate immediately (skip the visibility gate) - used for the first cells. */
  eager?: boolean;
};

const props = defineProps<Props>();
const emit = defineEmits<{ (e: "click", photo: PhotoDocument): void }>();

const photosStore = usePhotosStore();

const commentCount =
  (props.photoDoc as PhotoDocument & { number_of_comments?: number })
    .number_of_comments ?? 0;

const isReported = computed(
  () =>
    (props.photoDoc as PhotoDocument & { reported_by?: string[] }).reported_by
      ?.length ?? 0 > 0,
);

// Thumbnail blob is owned by the photos store (queued + cached across remounts).
const imageUrl = computed(
  () =>
    photosStore.getPhotoImage(
      props.groupId,
      props.photoDoc._id,
      ImageSizes.Thumb,
    )?.url ?? "",
);

// Until a cell has actually been in view, it stays a cheap placeholder: no
// blurhash decode, no <img>, no avatar. This keeps the (non-virtualized) grid
// cheap to mount so fast scrolling stays smooth.
const rootEl = ref<HTMLElement | null>(null);
const isVisible = useElementVisibility(rootEl, { delay: 50 });
const activated = ref(false);

// The blurhash is always shown (independent of activation). Eager cells decode
// it synchronously; the rest are decoded progressively in idle time so the grid
// stays cheap to mount yet never shows a bare grey cell during fast scrolls.
const hashUrl = ref("");
if (!isReported.value) {
  if (props.eager) {
    hashUrl.value = getBlurhashUrlSync(props.photoDoc?.blurhash) ?? "";
  } else {
    requestBlurhashUrl(props.photoDoc?.blurhash, (url) => {
      hashUrl.value = url;
    });
  }
}

// Activation gates the heavy parts: the network thumbnail and the avatar.
// Sticky: once activated we keep the content mounted across re-scrolls.
function activate(): void {
  activated.value = true;
  if (isReported.value) return;
  photosStore.loadPhotoImage(props.groupId, props.photoDoc, ImageSizes.Thumb);
}

if (props.eager) {
  activate();
}

watch(
  isVisible,
  (visible) => {
    if (visible) activate();
  },
  { immediate: true },
);
</script>

<template>
  <div
    ref="rootEl"
    class="grid-cell relative cursor-pointer overflow-hidden rounded-lg border-2 border-black-dark dark:border-kekkle-dark-light"
    @click="emit('click', photoDoc)"
  >
    <!-- Reported state -->
    <div
      v-if="isReported"
      class="flex aspect-square w-full flex-col items-center justify-center bg-yellow-100 dark:bg-yellow-900/30"
    >
      <ExclamationIcon class="h-8 w-8 text-yellow-600 dark:text-yellow-400" />
      <span
        class="mt-1 text-xs font-medium text-yellow-700 dark:text-yellow-400"
      >
        {{ $t("components.report.reportTitle") }}
      </span>
    </div>

    <!-- Normal image: thumbnail once loaded, else the (always shown) blurhash -->
    <template v-else>
      <img
        v-if="imageUrl"
        alt=""
        :src="imageUrl"
        class="aspect-square w-full object-cover"
      />
      <img
        v-else-if="hashUrl"
        alt=""
        :src="hashUrl"
        class="aspect-square w-full object-cover"
      />
      <div
        v-else
        class="aspect-square w-full animate-pulse bg-gray-200 dark:bg-kekkle-dark-light"
      />

      <!-- Overlay: avatar + comment count (deferred until the cell is in view) -->
      <div
        v-if="activated"
        class="absolute bottom-0 left-0 right-0 flex items-center justify-between bg-gradient-to-t from-black/50 to-transparent px-1.5 pb-1.5 pt-4"
      >
        <div class="grid-avatar">
          <TinyUserThumb :uuid="photoDoc.created_by" />
        </div>
        <div class="flex items-center gap-0.5">
          <ChatAlt2Icon class="h-3 w-3 text-white" />
          <span class="text-xs font-medium text-white">
            {{ commentCount }}
          </span>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
/* Skip rendering work for cells scrolled out of view (square ~1/3 viewport). */
.grid-cell {
  content-visibility: auto;
  contain-intrinsic-size: auto 120px;
}

.grid-avatar {
  width: 20px;
  height: 20px;
}

.grid-avatar :deep(div) {
  width: 20px !important;
  height: 20px !important;
}

.grid-avatar :deep(img) {
  width: 20px !important;
  height: 20px !important;
}
</style>
